// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import ActivityAvatar from 'app/common/react/components/Activities/ActivityItems/ActivityAvatar';
import ActivityItemConfirmModal from 'app/common/react/components/Activities/ActivityItems/ActivityItemConfirmModal';
import i18n from 'app/common/lib/i18n';
import MentionsInput from 'app/common/react/components/Mentions/MentionsInput';
import Testable from 'app/common/react/ui/Testable';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import withDragDropContext from 'app/common/lib/react/withDragDropContext';
import withViewer from 'app/common/lib/react/withViewer';
import { COMMENT_OBJECT_TYPES } from 'app/common/redux/reducers/aggregatedComments';
import { compose } from 'redux';
import { grabTags } from 'app/common/react/components/Mentions/common';
import { stripTags } from 'app/common/lib/StringUtil';
import { track } from 'app/common/lib/react/analytics';
import { withRouter } from 'react-router';
import {
  BOARD_ACTIVITY_OBJECT_TYPES,
  type ActivityUser,
  type ActivityItemType,
} from 'app/common/redux/reducers/activityItems';
import { Box, Button } from 'gestalt';

export type ActivityItemEditData = {
  contentMarkdown: string,
  imageSignatures: string,
  objectIds: Array<string>,
  tags: string,
};

type OwnProps = {|
  avatarSize?: 'sm' | 'md',
  shouldFocus: boolean,
  onAddItem: Function,
  onEditItem: Function,
  objectType: ActivityItemType,
  onCancelItem: Function,
  onEnter: Function,
  parentObjectId: string,
  privatePin?: boolean,
  replyToObject: Object,
  tags: Array<Object>,
  taggedUsers: Array<Object>,
  text: string,
  statusType: 'textInflightCreation' | 'textInFlightEdit' | 'textStaticDisplay',
  widthAuto: boolean,
|};

type ReduxProps = {|
  viewer: Viewer,
  // A transformed version of the viewer object that is consumable by ActivityAvatar
  viewingUser: ActivityUser,
|};

type HOCProps = {|
  router: Object,
|};

type ConnectedProps = {| ...OwnProps, ...ReduxProps |};

type Props = {| ...ConnectedProps, ...HOCProps |};

type State = {
  anchorRefSet: boolean,
  hasPostAction: boolean,
  inputAreaFocused: boolean,
  isTextChanged: boolean,
  showConfirmDiscardModal: boolean,
  tags: Array<Object>,
  taggedUserMap: Object,
  taggedUsers: Array<Object>,
  submissionInProgress: boolean,
  textValue: string,
  viewType: 'text',
};

const initialState = {
  anchorRefSet: false,
  hasPostAction: false,
  inputAreaFocused: false,
  isTextChanged: false,
  showConfirmDiscardModal: false,
  submissionInProgress: false,
  textValue: '',
  viewType: 'text',
};

const MAX_CHAR_LENGTH = 2000;

const addUserTag = (tags: Object, user: Object) => {
  tags[user.id] = user;
  return tags;
};

export class ActivityItemCreate extends Component<Props, State> {
  // $FlowFixMe: new errors found from flow 0.96 upgrade
  static defaultProps = {
    imageSignature: '',
    onAddPinModeChange: () => {},
    onEnter: () => {},
    showWelcomeEducation: false,
    text: '',
    widthAuto: false,
  };

  state = {
    ...initialState,
    tags: this.props.tags,
    taggedUserMap: {},
    taggedUsers: this.props.taggedUsers || [],
    textValue: this.props.text,
  };

  static getDerivedStateFromProps(props: Props, state: State) {
    if (props.replyToObject) {
      return {
        taggedUserMap: addUserTag(state.taggedUserMap, props.replyToObject.user),
      };
    }
    return {};
  }

  componentDidMount() {
    const { router } = this.props;

    // kathy: Our app is currently on v3 of react-router, and it looks like
    // setRouteLeaveHook is deprecated in v4 in favor of <Prompt>
    if (router.setRouteLeaveHook) {
      this._removeRouteLeaveHook = router.setRouteLeaveHook(
        router.routes[router.routes.length - 1],
        this.routerWillLeave,
      );
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { statusType, objectType, parentObjectId } = this.props;
    if (prevProps.statusType !== 'textInflightCreation' && statusType === 'textInflightCreation') {
      track(PCONST.thrift.EventType.CLICK, {
        element: this.composeContextLogElementMap[objectType],
        component: this.composeContextLogComponentMap[objectType],
        objectId: parentObjectId,
      });
    }
  }

  get isPost() {
    const { objectType } = this.props;
    return objectType === BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY;
  }

  get isBoardActivityComment() {
    return this.props.objectType === BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY_COMMENT;
  }

  get isAggregatedComment() {
    const { objectType } = this.props;
    return (
      objectType === COMMENT_OBJECT_TYPES.pin ||
      objectType === COMMENT_OBJECT_TYPES.didIt ||
      objectType === COMMENT_OBJECT_TYPES.aggregatedComment
    );
  }

  get composeContextLogComponentMap(): Object {
    return {
      [BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY_COMMENT]:
        PCONST.thrift.ComponentType.BOARD_ACTIVITY_COMMENT,
      [BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY]: PCONST.thrift.ComponentType.BOARD_ACTIVITY,
    };
  }

  get composeContextLogElementMap(): Object {
    return {
      [BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY_COMMENT]:
        PCONST.thrift.ElementType.BOARD_ACTIVITY_COMPOSE_BUTTON,
      [BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY]:
        PCONST.thrift.ElementType.BOARD_ACTIVITY_COMPOSE_BUTTON,
    };
  }

  setAnchorRef = (div: ?HTMLElement) => {
    this._suggestionAnchor = div;
    this.setState({ anchorRefSet: true });
  };

  setRef = (div: HTMLElement) => (this._textAreaRef = div);

  updateTags = (user: Object) => {
    const taggedUsers = this.state.taggedUsers.concat(user);
    const taggedUserMap = this.state.taggedUserMap;
    taggedUserMap[user.id] = addUserTag(user, this.state.taggedUserMap);
    this.setState({ taggedUserMap, taggedUsers });
  };

  _suggestionAnchor: ?HTMLElement;
  _textAreaRef: ?HTMLElement;
  _removeRouteLeaveHook: ?Function;

  // TODO(jwardle): This currently does not work as we have multiple ActivityItemCreates on a page and navgiation will
  // happen if any one of them returns true. This problem may have been solved in more recent versions of react-router
  // or we need the parent component to control the routerWillLeave listener
  routerWillLeave = () => {
    const { text: textInitial, statusType } = this.props;
    const { hasPostAction, textValue } = this.state;
    const discardText = i18n._('Discard unsaved changes?');

    if (statusType === 'textInFlightEdit') {
      if (!hasPostAction && textInitial !== textValue) {
        return discardText;
      } else {
        return true;
      }
    }

    if (!hasPostAction && textValue) {
      return discardText;
    }

    return true;
  };

  handleBlur = () => {
    this.setState({ inputAreaFocused: false });
  };

  handleFocus = () => {
    this.props.onEnter();
  };

  handleOnClickEdit = () => {
    const { onEditItem, taggedUsers } = this.props;
    const { taggedUserMap } = this.state;
    const tags = grabTags(this._textAreaRef, taggedUserMap, taggedUsers);
    const editData = {
      contentMarkdown: stripTags((this._textAreaRef && this._textAreaRef.textContent.trim()) || ''),
      imageSignatures: '',
      objectIds: [],
      tags,
    };
    this.setState({ hasPostAction: true });
    onEditItem(editData);
    this.setState({ submissionInProgress: false });
  };

  handleOnClickPost = () => {
    this.setState({ submissionInProgress: true });
    this.handleOnClickPostFinal();
  };

  handleOnClickPostFinal = () => {
    const { onAddItem, taggedUsers } = this.props;
    const tags = grabTags(this._textAreaRef, this.state.taggedUserMap, taggedUsers);

    const createData = {
      contentMarkdown: stripTags((this._textAreaRef && this._textAreaRef.textContent.trim()) || ''),
      imageSignatures: '',
      objectIds: [],
      tags,
    };

    this.setState({ hasPostAction: true });
    onAddItem(createData);
    this.setState(initialState);
  };

  handleOnFinalDiscardItem = () => {
    // As the edit is being discarded and component unmounted,
    // we should remove the hook that checks for edits on navigate
    if (this._removeRouteLeaveHook) {
      this._removeRouteLeaveHook();
    }
    const { onCancelItem } = this.props;

    this.setState(initialState);
    onCancelItem();
  };

  handleTextChange = (isTextChanged: boolean) =>
    this.setState({
      isTextChanged: !!(this._textAreaRef && this._textAreaRef.textContent.trim()) && isTextChanged,
    });

  isSubmitEditDisabled = () => {
    const { isTextChanged } = this.state;
    return !isTextChanged;
  };

  toggleShowConfirmDiscardModal = () => {
    const { showConfirmDiscardModal } = this.state;
    this.setState({ showConfirmDiscardModal: !showConfirmDiscardModal });
  };

  renderConfirmDiscardModal = () => {
    const { statusType } = this.props;
    let detail;
    let confirmButtonText;

    if (statusType === 'textInFlightEdit') {
      detail = i18n._(
        'Your changes will be discarded',
        'Confirmation dialog before discarding changes',
      );
      confirmButtonText = i18n._('Discard changes', 'Clear edits');
    } else {
      detail = i18n._(
        'Once you delete a draft, you can’t undo it',
        'Confirmation before discarding changes',
      );

      confirmButtonText = i18n._('Delete', 'delete draft of a post or comment');
    }

    return (
      <ActivityItemConfirmModal
        accessibilityModalLabel={i18n._(
          'Confirm discard composer',
          'Confirm discarding a post or comment',
        )}
        heading={i18n._('Are you sure?', 'Confirm discarding a post or comment')}
        cancelButtonText={i18n._('Cancel', 'Cancel discarding a post or comment')}
        confirmButtonText={confirmButtonText}
        detail={detail}
        onConfirm={this.handleOnFinalDiscardItem}
        onDismiss={this.toggleShowConfirmDiscardModal}
      />
    );
  };

  render() {
    const { avatarSize, replyToObject, privatePin, statusType, viewingUser } = this.props;
    const {
      anchorRefSet,
      inputAreaFocused,
      isTextChanged,
      showConfirmDiscardModal,
      submissionInProgress,
      tags,
      taggedUsers,
      textValue,
    } = this.state;
    const inputContainerFocusedStyle = {
      borderRadius: 8,
      boxShadow: '0 0 0 4px rgba(0,132,255,.5)',
    };

    const textAreaStyle = {
      background: 'transparent',
      border: '0',
      display: isTextChanged ? 'inline-block' : 'block',
      minHeight: statusType === 'textStaticDisplay' ? '' : '50px',
      fontSize: '16px',
      outline: '0',
      overflowX: 'hidden',
      resize: 'none',
      whiteSpace: 'pre-wrap',
      width: '100%',
      wordBreak: 'break-all',
    };

    // TODO(jwardle): Hand these strings down as props
    let placeholderString;
    if (this.isPost) {
      placeholderString = i18n._('Start a discussion', 'Writing a post');
    } else if (this.isAggregatedComment && privatePin) {
      placeholderString = i18n._('Add a public comment', 'Add a comment');
    } else if (this.isAggregatedComment) {
      placeholderString = i18n._('Add a comment', 'Prompt to add a comment');
    } else if (this.isBoardActivityComment) {
      placeholderString = i18n._('Add comment', 'Prompt to add a comment');
    } else {
      placeholderString = i18n._('Reply', 'Adding text to a community comment');
    }

    let createButtonString;
    if (this.isPost) {
      createButtonString = i18n._('Post', 'Create a post');
    } else if (this.isAggregatedComment) {
      createButtonString = i18n._('Done', 'Post a top level comment on a pin or tried it');
    } else {
      createButtonString = i18n._('Reply', 'Posting a reply to a post or comment');
    }

    const isChanged = isTextChanged;

    if (statusType === 'textInflightCreation') {
      return (
        <Box column={12} display="flex" justifyContent="center">
          <Box width="100%">
            <Box display="flex">
              <Box width={40} marginRight={2}>
                <ActivityAvatar user={viewingUser} size={avatarSize} />
              </Box>
              <Box
                dangerouslySetInlineStyle={{
                  __style: { borderRadius: '16px', border: '1px solid #EFEFEF' },
                }}
                flex="grow"
                padding={4}
                width="100%"
              >
                <div
                  ref={this.setAnchorRef}
                  style={inputAreaFocused ? inputContainerFocusedStyle : {}}
                >
                  <Box display="flex" shape="rounded">
                    {anchorRefSet && (
                      <MentionsInput
                        shouldLoseFocus={showConfirmDiscardModal}
                        handleBlur={this.handleBlur}
                        style={textAreaStyle}
                        setRef={this.setRef}
                        suggestionAnchor={this._suggestionAnchor}
                        updateTags={this.updateTags}
                        existingTags={tags}
                        replyToObject={replyToObject}
                        taggedUsers={taggedUsers}
                        textValue={textValue}
                        onChange={this.handleTextChange}
                      />
                    )}
                  </Box>
                </div>
              </Box>
            </Box>
            <Box display="flex" justifyContent="end" marginTop={4}>
              <Button
                color="gray"
                inline
                onClick={
                  isChanged ? this.toggleShowConfirmDiscardModal : this.handleOnFinalDiscardItem
                }
                text={i18n._('Cancel', 'Cancel making post or comment')}
              />
              <Box marginLeft={2}>
                <Testable id="activity-item-create-submit">
                  <Button
                    color="red"
                    disabled={!isTextChanged || submissionInProgress}
                    inline
                    onClick={this.handleOnClickPost}
                    text={createButtonString}
                  />
                </Testable>
              </Box>
            </Box>
          </Box>
          {showConfirmDiscardModal && this.renderConfirmDiscardModal()}
        </Box>
      );
    }

    if (statusType === 'textInFlightEdit') {
      return (
        <Box column={12} display="flex" justifyContent="center">
          <Box width="100%">
            <Box display="flex">
              <Box
                dangerouslySetInlineStyle={{
                  __style: { borderRadius: '16px', border: '1px solid #EFEFEF' },
                }}
                width="100%"
                padding={4}
              >
                <div
                  ref={this.setAnchorRef}
                  style={inputAreaFocused ? inputContainerFocusedStyle : {}}
                >
                  <Box display="flex" shape="rounded">
                    {anchorRefSet && (
                      <MentionsInput
                        handleBlur={this.handleBlur}
                        style={textAreaStyle}
                        setRef={this.setRef}
                        suggestionAnchor={this._suggestionAnchor}
                        updateTags={this.updateTags}
                        existingTags={tags}
                        taggedUsers={taggedUsers}
                        textValue={textValue}
                        onChange={this.handleTextChange}
                      />
                    )}
                  </Box>
                </div>
              </Box>
            </Box>
            <Box display="flex" justifyContent="end" marginTop={4}>
              <Button
                color="gray"
                inline
                onClick={
                  isChanged ? this.toggleShowConfirmDiscardModal : this.handleOnFinalDiscardItem
                }
                text={i18n._('Cancel', 'Cancel making post or comment')}
              />
              <Box marginLeft={2}>
                <Button
                  color="red"
                  disabled={this.isSubmitEditDisabled() || submissionInProgress}
                  inline
                  onClick={this.handleOnClickEdit}
                  text={i18n._('Edit', 'Edit a post')}
                />
              </Box>
            </Box>
          </Box>
          {showConfirmDiscardModal && this.renderConfirmDiscardModal()}
        </Box>
      );
    }

    return (
      <Box alignItems="center" display="flex" justifyContent="between" shape="rounded">
        <div
          onKeyDown={this.handleFocus}
          onClick={this.handleFocus}
          role="textbox"
          tabIndex="0"
          style={{ cursor: 'text', width: '100%' }}
        >
          <Box column={12} display="flex">
            <Box width={40}>
              <ActivityAvatar user={viewingUser} size={avatarSize} />
            </Box>
            <Box
              dangerouslySetInlineStyle={{
                __style: { borderRadius: '26px', border: '1px solid #EFEFEF' },
              }}
              width="100%"
              height={40}
              alignItems="center"
              display="flex"
              marginLeft={2}
              padding={4}
            >
              <textarea
                maxLength={MAX_CHAR_LENGTH}
                name="communityItemTextBox"
                onBlur={this.handleBlur}
                onFocus={this.handleFocus}
                placeholder={placeholderString}
                ref={(comp) => {
                  this._textAreaRef = comp;
                }}
                rows={1}
                style={textAreaStyle}
              />
            </Box>
          </Box>
        </div>
      </Box>
    );
  }
}

const viewingUser = {
  image_medium_url: '',
  type: 'user',
  username: '',
  full_name: '',
  first_name: '',
  id: '',
};

const mapStateToProps = (state): ReduxProps => {
  const { viewer } = state;
  viewingUser.image_medium_url = viewer.imageMediumUrl;
  viewingUser.username = viewer.username;
  viewingUser.full_name = viewer.fullName;
  return { viewer, viewingUser };
};

export default compose(
  connect<ConnectedProps, OwnProps, _, _, _, _>(
    mapStateToProps,
    () => ({}),
  ),
  withDragDropContext,
  withViewer,
  withRouter,
)(ActivityItemCreate);
